import * as _ from "lodash";

import {WB_ConsumptionProductGroupRow} from "../PA.Core/PAdbSchemaRows";
import {IConsumptionAdjustmentsCalculator, PAConsumptionAdjustmentCalcOptions} from "./PA.Consumption.Services.IF";
import {BehaviorSubject, Subscription} from "rxjs";
import {IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {RecordQueue} from "../PA.Core/Basic/RecordQueue";
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {MetricParameters, MetricsParameterSet, MetricsSet} from "../PA.Metric.Services/PAMetrics";
import {PAMetricServiceClient} from "../PA.Metric.Services/PAMetricServiceClient";
import {ConsumptionAdjustmentsFactory} from "../PA.Consumption.Core/ConsumptionAdjustmentsFactory";

const bDebugSerializers = true
const bDisableSyndicatedConsumptionMetricCalc = true

interface ICalcStepOptions {
  bUseProgressProvider?: boolean,
  bUseMessageProvider?: boolean
}

const CalcStepOptionsDefaults: ICalcStepOptions = {
  bUseProgressProvider: true,
  bUseMessageProvider: false
}


export class ConsumptionAdjustmentsCalculator
  implements IConsumptionAdjustmentsCalculator {
  private dctCnsPG: { [id: number]: WB_ConsumptionProductGroupRow }
  private queueCnsPGMetricCalc: RecordQueue<WB_ConsumptionProductGroupRow>
  private queueCnsPGAdjustCalc: RecordQueue<WB_ConsumptionProductGroupRow>

  private _consumptionAdjustmentsFactory: ConsumptionAdjustmentsFactory = undefined
  private _subOnFactoryLoadComplete: Subscription = undefined

  private _enabled: boolean

  private _CalcAdjustmentsProgressProvider: IProgressProvider;
  private _CalcMessageProvider: IMessageProvider;
  private _CalcMetricsProgressProvider: IProgressProvider;

  private _qsAdjustment_CalcQueue: ExecutionSerializer
  private _qsMetrics_CalcQueue: ExecutionSerializer

  private _OnCalculateAdjustmentsComplete: BehaviorSubject<IConsumptionAdjustmentsCalculator>;
  private _OnCalculateMetricsComplete: BehaviorSubject<IConsumptionAdjustmentsCalculator>;


  constructor() {

    this._enabled = false
    this.dctCnsPG = {}
    this.queueCnsPGMetricCalc = new RecordQueue<WB_ConsumptionProductGroupRow>("WB_CNSPG_ID")
    this.queueCnsPGAdjustCalc = new RecordQueue<WB_ConsumptionProductGroupRow>("WB_CNSPG_ID")

    this._CalcAdjustmentsProgressProvider = new ProgressProvider()
    this._CalcMessageProvider = new MessageProvider()
    this._CalcMetricsProgressProvider = new ProgressProvider()

    this._qsAdjustment_CalcQueue = new ExecutionSerializer(this, this.calcAdjustmentQueue.bind(this),
      [{}], 1000, bDebugSerializers)

    this._qsMetrics_CalcQueue = new ExecutionSerializer(this, this.calcMetricsQueue.bind(this),
      [{}], 500, bDebugSerializers)


    this._OnCalculateAdjustmentsComplete = new BehaviorSubject<IConsumptionAdjustmentsCalculator>(this)
    this._OnCalculateMetricsComplete = new BehaviorSubject<IConsumptionAdjustmentsCalculator>(this)
  }

  Register(caf: ConsumptionAdjustmentsFactory) {
    //
    this.UnRegister()
    this._consumptionAdjustmentsFactory = caf
    this._subOnFactoryLoadComplete = caf.OnLoaded.subscribe(this.AdjustmentFactory_OnLoad.bind(this))
  }

  UnRegister() {
    this._consumptionAdjustmentsFactory = undefined
    if (this._subOnFactoryLoadComplete) {
      this._subOnFactoryLoadComplete.unsubscribe()
    }
  }


  get CalcAdjustmentsProgressProvider(): IProgressProvider {
    return this._CalcAdjustmentsProgressProvider
  }

  get CalcMessageProvider(): IMessageProvider {
    return this._CalcMessageProvider
  }

  get CalcMetricsProgressProvider(): IProgressProvider {
    return this._CalcMetricsProgressProvider
  }

  get Enabled(): boolean {
    return this._enabled
  }

  set Enabled(value) {
    this._enabled = value
  }

  get OnCalculateAdjustmentsComplete(): BehaviorSubject<IConsumptionAdjustmentsCalculator> {
    return this._OnCalculateAdjustmentsComplete
  }

  get OnCalculateMetricsComplete(): BehaviorSubject<IConsumptionAdjustmentsCalculator> {
    return this._OnCalculateMetricsComplete
  }


  CalculateAdjustments(options?: PAConsumptionAdjustmentCalcOptions): Promise<void> {
    let cnsPG = this.getQueueRecordsFromOptions(options)
    this.queueCnsPGAdjustCalc.queue(...cnsPG)
    return this.calcAdjustmentQueue();
  }

  CalculateMetrics(options?: PAConsumptionAdjustmentCalcOptions): Promise<void> {
    let cnsPG = this.getQueueRecordsFromOptions(options)
    this.queueCnsPGMetricCalc.queue(...cnsPG)
    return this.calcMetricsQueue();
  }

  QueueCalculateAdjustments(options?: PAConsumptionAdjustmentCalcOptions): void {
    let cnsPG = this.getQueueRecordsFromOptions(options)
    this.queueCnsPGAdjustCalc.queue(...cnsPG)
    this._qsAdjustment_CalcQueue.Run()
  }

  QueueCalculateMetrics(options?: PAConsumptionAdjustmentCalcOptions): void {
    let cnsPG = this.getQueueRecordsFromOptions(options)
    this.queueCnsPGMetricCalc.queue(...cnsPG)
    this._qsMetrics_CalcQueue.Run()
  }

  private getQueueRecordsFromOptions(options?: PAConsumptionAdjustmentCalcOptions): WB_ConsumptionProductGroupRow[] {
    const self = this
    let opts = options || {}

    // Queue all the CnsPG
    let cnsPG
      = self._consumptionAdjustmentsFactory.Find(opts.PlanningCustomers || [],
                                                  opts.SourceCustomers || [],
                                                              opts.ProductGroups || [],
                                                                             opts.StartDt, opts.EndDt)

    return cnsPG
  }


  //<editor-fold desc="Event Handlers">

  private AdjustmentFactory_OnLoad(arrCPGs: WB_ConsumptionProductGroupRow[]): void {
    let queueChanged = false
    // For each record not already calcled, queue it for calculations
    _.forEach(arrCPGs, (aCnsPG)=> {
      //
      let key = aCnsPG.cif.WB_CNSPG_ID
      if(key in this.dctCnsPG){ return }
      aCnsPG.setupOnChangeEvents(["BS_BASE_QTY_OVRD", "IS_INCR_SHIFT_QTY_PREV", "IS_INCR_SHIFT_QTY_NEXT"],this.ConsumptionProductGroup_OnChange.bind(this), {})
      this.dctCnsPG[key] = aCnsPG
      this.queueCnsPGMetricCalc.queue(aCnsPG)
      queueChanged = true
    })

    if(queueChanged){
      this._qsMetrics_CalcQueue.Run()
    }
  }

  private ConsumptionProductGroup_OnChange(record: WB_ConsumptionProductGroupRow, propertyName: string, propertyValue: any, options: any): void {

    //

    switch(propertyName){
      case "BS_BASE_QTY_OVRD":

        //console.log("CalcChange: ", propertyName, " record: " ,record)
        this.queueCnsPGAdjustCalc.queue(record)


        if(!_.isEmpty(record.WB_ConsumptionProductGroupRow_Next)){
          this.queueCnsPGAdjustCalc.queue(record.WB_ConsumptionProductGroupRow_Next)
        }

        if(!_.isEmpty(record.WB_ConsumptionProductGroupRow_Prev)) {
          this.queueCnsPGAdjustCalc.queue(record.WB_ConsumptionProductGroupRow_Prev)
        }

        this._qsAdjustment_CalcQueue.Run()
        break;

      case "BS_PROMOBASE_QTY_OVRD":
        break;

      case "IS_INCR_SHIFT_QTY_PREV":
      case "IS_INCR_SHIFT_QTY_NEXT":

        this.queueCnsPGAdjustCalc.queue(record)

        if(!_.isEmpty(record.WB_ConsumptionProductGroupRow_Next)){
          this.queueCnsPGAdjustCalc.queue(record.WB_ConsumptionProductGroupRow_Next)
        }

        if(!_.isEmpty(record.WB_ConsumptionProductGroupRow_Prev)){
          this.queueCnsPGAdjustCalc.queue(record.WB_ConsumptionProductGroupRow_Prev)
        }
        this._qsAdjustment_CalcQueue.Run()
        break;

    }


  }


    //</editor-fold>


  //<editor-fold desc="Metrics Calculation Support Functions">

  private async calcMetricsQueue() {
    const self = this

    while(self.queueCnsPGMetricCalc.queueLength() > 0){

      try {
        let workingSet = self.queueCnsPGMetricCalc.dequeue(100)
        if(!bDisableSyndicatedConsumptionMetricCalc){
          let mps = self.createMetricParameterSet(workingSet)
          let aMetricService = new PAMetricServiceClient()
          let metricSet = await aMetricService.calculateMetricSet(mps)
          self.processMetricsSet(workingSet, metricSet)
        }
      }
      catch (err) {
        throw new Error(err);
      }

    }

    self.OnCalculateMetricsComplete.next(self)


  }

  private createMetricParameterSet(arrWBCnsPG: WB_ConsumptionProductGroupRow[]): MetricsParameterSet {
    let returnValue = new MetricsParameterSet()

    _.forEach(arrWBCnsPG, (record) => {

      let aMP = new MetricParameters()
      aMP.MetricParameterId = record.cif.WB_CNSPG_ID + ''
      aMP.bCalculateIntersection = false
      aMP.PlanningCustomers.push(record.cif.WB_CUST_ID_PLANNING)
      aMP.SourceCustomers.push(record.cif.WB_CUST_ID_SOURCE)
      aMP.ConsumptionAdjustmentsPGs.push(record.cif.WB_CNSPG_ID)
      aMP.InStoreStartDt = record.DateRange.Range.StartDt
      aMP.InStoreEndDt = record.DateRange.Range.EndDt

      aMP.bIncludeAdjustments = false
      aMP.bIncludeEDLPSplit = false
      returnValue[aMP.MetricParameterId] = aMP

    })


    return returnValue
  }


  private processMetricsSet(arrWBCnsPG: WB_ConsumptionProductGroupRow[], ms: MetricsSet ){

    _.forEach(arrWBCnsPG, (aCnsPG) => {
      let key = aCnsPG.cif.WB_CNSPG_ID + ''
      let mtr = ms[key]

      if(_.isEmpty(mtr)){ return }

      let rec = aCnsPG.cif
      rec.SYND_BASE_PRICE = mtr.RETAILER_BASE_PRICE
      rec.SYND_PROMOTED_PRICE = mtr.RETAILER_PROMOTED_PRICE
      rec.SYND_COVERAGE_FACTOR = 1
      rec.SYND_CNS_BASE_UNITS = mtr.CNSMP_SYND_BASE_UNITS
      rec.SYND_CNS_BASE_WT = mtr.CNSMP_SYND_BASE_WT
      rec.SYND_CNS_BASE_STAT = mtr.CNSMP_SYND_BASE_STAT
      rec.SYND_CNS_BASE_RTLSLS = mtr.CNSMP_SYND_BASE_RTLSLS

      rec.SYND_CNS_PROMOBASE_UNITS = mtr.CNSMP_PROMO_UNITS - mtr.CNSMP_INCR_UNITS
      rec.SYND_CNS_PROMOBASE_WT = mtr.CNSMP_PROMO_WT - mtr.CNSMP_INCR_WT
      rec.SYND_CNS_PROMOBASE_STAT = mtr.CNSMP_PROMO_STAT - mtr.CNSMP_INCR_STAT
      rec.SYND_CNS_PROMOBASE_RTLSLS = mtr.CNSMP_PROMO_RTLSLS - mtr.CNSMP_INCR_RTLSLS

      rec.SYND_CNS_INCR_UNITS = mtr.CNSMP_INCR_UNITS
      rec.SYND_CNS_INCR_WT = mtr.CNSMP_INCR_WT
      rec.SYND_CNS_INCR_STAT = mtr.CNSMP_INCR_STAT
      rec.SYND_CNS_INCR_RTLSLS = mtr.CNSMP_INCR_RTLSLS

      rec.SYND_CNS_TOTAL_UNITS = mtr.CNSMP_TOTAL_UNITS
      rec.SYND_CNS_TOTAL_WT = mtr.CNSMP_TOTAL_WT
      rec.SYND_CNS_TOTAL_STAT = mtr.CNSMP_TOTAL_STAT
      rec.SYND_CNS_TOTAL_RTLSLS = mtr.CNSMP_TOTAL_RTLSLS

      rec.QTY_UOM = "EA"
      rec.QTY_UOM_TO_UNITS = 1
      rec.WT_UOM_PER_UNIT = ((mtr.CNSMP_TOTAL_UNITS > 0)? mtr.CNSMP_TOTAL_WT / mtr.CNSMP_TOTAL_UNITS : 0)
      rec.STAT_UOM_PER_UNIT = ((mtr.CNSMP_TOTAL_UNITS > 0)? mtr.CNSMP_TOTAL_STAT / mtr.CNSMP_TOTAL_UNITS : 0)
      rec.RTLSLS_UOM_PER_UNIT = ((mtr.CNSMP_TOTAL_UNITS > 0)? mtr.CNSMP_TOTAL_RTLSLS / mtr.CNSMP_TOTAL_UNITS : 0)
      rec.RTLSLS_UOM_PER_BASE_UNIT = ((mtr.CNSMP_SYND_BASE_UNITS > 0)? mtr.CNSMP_SYND_BASE_RTLSLS / mtr.CNSMP_SYND_BASE_UNITS : rec.RTLSLS_UOM_PER_UNIT)
      rec.RTLSLS_UOM_PER_INCR_UNIT = ((mtr.CNSMP_INCR_UNITS > 0)? mtr.CNSMP_INCR_RTLSLS / mtr.CNSMP_INCR_UNITS : rec.RTLSLS_UOM_PER_UNIT)

    })

  }

  //</editor-fold>

  //<editor-fold desc="Metrics Adjustments Support Functions">

  private async calcAdjustmentQueue() {
    const self = this

    while(self.queueCnsPGAdjustCalc.queueLength() > 0){

      try {
        let workingSet = self.queueCnsPGAdjustCalc.dequeue(100)
        self.processAdjustments(workingSet)
      }
      catch (err) {
        throw new Error(err);
      }

    }
    self.OnCalculateAdjustmentsComplete.next(self)


  }

  private processAdjustments(arrWBCnsPG: WB_ConsumptionProductGroupRow[]){

    _.forEach(arrWBCnsPG, (aCnsPG) => {
      this.ConsumptionPG_calc(aCnsPG)
    })

  }

  private ConsumptionPG_calc(aCnsPG: WB_ConsumptionProductGroupRow){

    let rec = aCnsPG.cif

    // A. Calculate Base Split Adjustment
    let BsOvrd = rec.BS_BASE_QTY_OVRD

    // OVRD QTY UOM = EA
    // Policy.. convserver total volume until Base Ovrd > Total Qty
    if(BsOvrd > 0){
      rec.BS_BASE_QTY_ADJ = BsOvrd - rec.SYND_CNS_BASE_UNITS
      rec.BS_CNS_BASE_UNITS = rec.BS_BASE_QTY_ADJ
      if(BsOvrd > rec.SYND_CNS_TOTAL_UNITS){
        rec.BS_CNS_INCR_UNITS = -1 * rec.SYND_CNS_INCR_UNITS
      } else {
        rec.BS_CNS_INCR_UNITS = -1 * rec.BS_BASE_QTY_ADJ
      }
    } else {
      rec.BS_BASE_QTY_ADJ = 0
      rec.BS_CNS_BASE_UNITS = 0
      rec.BS_CNS_INCR_UNITS = 0
    }


    let BsPrOvrd = rec.BS_PROMOBASE_QTY_OVRD
    if(BsPrOvrd > 0){
      rec.BS_PROMOBASE_QTY_ADJ = BsPrOvrd - rec.SYND_CNS_PROMOBASE_UNITS
      rec.BS_CNS_PROMOBASE_UNITS = rec.BS_PROMOBASE_QTY_ADJ
    } else {
      rec.BS_PROMOBASE_QTY_ADJ = 0
      rec.BS_CNS_PROMOBASE_UNITS = 0
    }

    rec.BS_CNS_BASE_WT     = rec.BS_CNS_BASE_UNITS * rec.WT_UOM_PER_UNIT
    rec.BS_CNS_BASE_STAT   = rec.BS_CNS_BASE_UNITS * rec.STAT_UOM_PER_UNIT
    rec.BS_CNS_BASE_RTLSLS = rec.BS_CNS_BASE_UNITS * rec.RTLSLS_UOM_PER_BASE_UNIT

    rec.BS_CNS_PROMOBASE_WT     = rec.BS_CNS_PROMOBASE_UNITS * rec.WT_UOM_PER_UNIT
    rec.BS_CNS_PROMOBASE_STAT   = rec.BS_CNS_PROMOBASE_UNITS * rec.STAT_UOM_PER_UNIT
    rec.BS_CNS_PROMOBASE_RTLSLS = rec.BS_CNS_PROMOBASE_UNITS * rec.RTLSLS_UOM_PER_UNIT

    rec.BS_CNS_INCR_WT          = rec.BS_CNS_INCR_UNITS * rec.WT_UOM_PER_UNIT
    rec.BS_CNS_INCR_STAT        = rec.BS_CNS_INCR_UNITS * rec.STAT_UOM_PER_UNIT
    rec.BS_CNS_INCR_RTLSLS      = rec.BS_CNS_INCR_UNITS * rec.RTLSLS_UOM_PER_UNIT


    // B. Calculate Incremental Adjustment
    let IncrQty_Prev = Math.max(rec.IS_INCR_SHIFT_QTY_PREV,0)
    let IncrQty_Next = Math.max(rec.IS_INCR_SHIFT_QTY_NEXT,0)

    // -- don't validate the total amount to being moved out.
    rec.IS_CNS_INCR_UNITS_OUT = -1 * (IncrQty_Prev + IncrQty_Next)
    rec.IS_CNS_INCR_WT_OUT = rec.IS_CNS_INCR_UNITS_OUT * rec.WT_UOM_PER_UNIT
    rec.IS_CNS_INCR_STAT_OUT = rec.IS_CNS_INCR_UNITS_OUT * rec.STAT_UOM_PER_UNIT
    rec.IS_CNS_INCR_RTLSLS_OUT = rec.IS_CNS_INCR_UNITS_OUT * rec.RTLSLS_UOM_PER_INCR_UNIT


    if(aCnsPG.WB_ConsumptionProductGroupRow_Prev){
      let recPrev = aCnsPG.WB_ConsumptionProductGroupRow_Prev.cif

      recPrev.IS_CNS_INCR_UNITS_NEXT  = IncrQty_Prev
      recPrev.IS_CNS_INCR_WT_NEXT     = recPrev.IS_CNS_INCR_UNITS_NEXT * rec.WT_UOM_PER_UNIT
      recPrev.IS_CNS_INCR_STAT_NEXT   = recPrev.IS_CNS_INCR_UNITS_NEXT * rec.STAT_UOM_PER_UNIT
      recPrev.IS_CNS_INCR_RTLSLS_NEXT = recPrev.IS_CNS_INCR_UNITS_NEXT * rec.RTLSLS_UOM_PER_INCR_UNIT
    }

    if(aCnsPG.WB_ConsumptionProductGroupRow_Next){
      let recNext = aCnsPG.WB_ConsumptionProductGroupRow_Next.cif

      recNext.IS_CNS_INCR_UNITS_PREV  = IncrQty_Next
      recNext.IS_CNS_INCR_WT_PREV     = recNext.IS_CNS_INCR_UNITS_PREV * rec.WT_UOM_PER_UNIT
      recNext.IS_CNS_INCR_STAT_PREV   = recNext.IS_CNS_INCR_UNITS_PREV * rec.STAT_UOM_PER_UNIT
      recNext.IS_CNS_INCR_RTLSLS_PREV = recNext.IS_CNS_INCR_UNITS_PREV * rec.RTLSLS_UOM_PER_INCR_UNIT
    }

  }

  //</editor-fold>


}

